import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { AppLayout } from '@/components/layout/AppLayout'
import { LoginPage } from '@/pages/LoginPage'
import { SignupPage } from '@/pages/SignupPage'
import { LandingPage } from '@/pages/LandingPage'
import { TermsPage } from '@/pages/TermsPage'
import { CommercePage } from '@/pages/CommercePage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ProjectPage } from '@/pages/ProjectPage'
import { EndpointPage } from '@/pages/EndpointPage'
import { SharedPage } from '@/pages/SharedPage'
import { GalleryPage } from '@/pages/GalleryPage'
import { BillingPage } from '@/pages/BillingPage'
import { DocsPage, DocsContent } from '@/pages/DocsPage'
import { AccountPage } from '@/pages/AccountPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'

// slug.lvh.me / slug.scenariomock.com → 共有ページ
// (本番の *.scenariomock.com は Fly の Mock サーバーが応答するため、実際にここへ来るのは主にローカル開発)
// localhost, app.* → 管理アプリ
function extractSlugFromHostname(hostname: string): string | null {
  const bare = hostname.split(':')[0]
  if (bare === 'localhost' || bare === '127.0.0.1') return null
  if (bare.startsWith('app.')) return null
  const match = bare.match(/^([a-z0-9-]+)\.(lvh\.me|scenariomock\.com)$/)
  if (!match) return null
  if (['app', 'www'].includes(match[1])) return null
  return match[1]
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const { t } = useTranslation()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="text-muted-foreground">{t('common.loading')}</span>
      </div>
    )
  }

  if (!user) {
    // /docs は未ログインでも公開ページとして表示する
    if (location.pathname === '/docs') {
      return <DocsPage />
    }
    return <Navigate to="/login" replace />
  }

  // ドメイン未登録はアカウント設定画面へ強制遷移
  if (!user.domain && location.pathname !== '/account') {
    return <Navigate to="/account" replace state={{ required: true }} />
  }

  return <>{children}</>
}

function App() {
  // slug サブドメインからアクセスされた場合はそのまま SharedPage を表示
  const slugFromHost = extractSlugFromHostname(window.location.hostname)
  if (slugFromHost) {
    return <SharedPage slug={slugFromHost} />
  }

  // ルートを追加したら public/_redirects にも追加する(SPA シェルへの振り分け)
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/legal/commerce" element={<CommercePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/shared/:slug" element={<SharedPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/docs" element={<DocsContent />} />
        <Route path="/projects/:projectId" element={<ProjectPage />} />
        <Route path="/projects/:projectId/endpoints/:endpointId" element={<EndpointPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
